import { jsonBody, requestJson } from "../../api/http/httpClient";
import {
  readArray,
  readBoolean,
  readInteger,
  readNullableInteger,
  readNullableString,
  readObject,
  readOneOf,
  readString,
  type JsonObject,
} from "../../api/http/jsonDecoding";

/**
 * Server keys, their budgets and use, and who has a "DMW server" (ADR-0023, MA-C4; built
 * provisionally, awaiting acceptance): account administrators only. A key goes in once and
 * never comes back; only its last four characters are shown. Who may use the keys is each
 * project's rule, set in its settings. Routes: `server/src/dmw/api/routes/server_keys/__init__.py`.
 */

export const API_KINDS = ["openai", "anthropic", "gemini", "openai_compatible"] as const;
export type ApiKind = (typeof API_KINDS)[number];
const KEY_STATES = ["active", "revoked"] as const;
export const SPEND_PROVIDERS = ["openai", "anthropic"] as const;
export type SpendProvider = (typeof SPEND_PROVIDERS)[number];
const LIMIT_SOURCES = ["provider", "stated"] as const;

export interface KeyBudget {
  /** Provider calls per calendar month (UTC); `null`: no limit. */
  readonly budgetCalls: number | null;
  readonly budgetTokens: number | null;
}

/** DMW's own count of a key's use this month. */
export interface KeyUsage {
  /** `YYYY-MM`; empty before the key's first turn. */
  readonly period: string;
  readonly usedCalls: number;
  readonly usedTokens: number;
  readonly reservedCalls: number;
  /** Of the used calls, those the provider reported nothing for, charged at their reservation. */
  readonly unknownCalls: number;
}

export interface ServerKey extends KeyBudget {
  readonly keyId: string;
  readonly routeId: string;
  readonly api: string;
  readonly baseUrl: string | null;
  readonly models: readonly string[];
  readonly hint: string;
  readonly state: (typeof KEY_STATES)[number];
  readonly usage: KeyUsage;
  readonly addedAt: string;
}

/** A person's "DMW server": it runs their turns on the keys while a project lets them use them. */
export interface ServerHost {
  readonly userId: string;
  readonly userName: string | null;
  readonly online: boolean;
  readonly pairedAt: string;
}

export interface ServerKeysOverview {
  readonly available: boolean;
  readonly offReason: string | null;
  readonly keys: readonly ServerKey[];
  readonly hosts: readonly ServerHost[];
}

export interface NewServerKey extends KeyBudget {
  readonly routeId: string;
  readonly api: ApiKind;
  readonly baseUrl: string | null;
  readonly apiKey: string;
}

/** One provider's own spend this month, read with an administrator's admin key; monitoring only. */
export interface ProviderSpend {
  readonly provider: SpendProvider;
  /** `organization`, or the OpenAI project read. */
  readonly scope: string;
  readonly month: string;
  /** Decimal text in US dollars; `null`: not read, `spendProblem` says why. */
  readonly spentUsd: string | null;
  readonly spendProblem: string | null;
  readonly limitUsd: string | null;
  /** `provider`: read from the provider; `stated`: typed into DMW's configuration. */
  readonly limitSource: (typeof LIMIT_SOURCES)[number] | null;
  readonly limitProblem: string | null;
  readonly readAt: string;
}

export interface ProviderSpendOverview {
  /** The providers whose admin key is set up. */
  readonly configured: readonly SpendProvider[];
  readonly providers: readonly ProviderSpend[];
}

export function readServerKeys(signal?: AbortSignal): Promise<ServerKeysOverview> {
  return requestJson({ method: "GET", path: "/server-keys", signal, decodeResponse: decodeOverview });
}

export function readProviderSpend(signal?: AbortSignal): Promise<ProviderSpendOverview> {
  return requestJson({ method: "GET", path: "/server-keys/provider-spend", signal, decodeResponse: decodeProviderSpend });
}

export function addServerKey(key: NewServerKey): Promise<ServerKey> {
  return requestJson({
    method: "POST",
    path: "/server-keys/keys",
    body: jsonBody({
      route_id: key.routeId,
      api: key.api,
      base_url: key.baseUrl,
      ...budgetBody(key),
      api_key: key.apiKey,
    }),
    decodeResponse: (body) => decodeKey(readObject(body, "server key")),
  });
}

export function changeKeyBudget(keyId: string, budget: KeyBudget): Promise<ServerKey> {
  return requestJson({
    method: "PUT",
    path: `/server-keys/keys/${encodeURIComponent(keyId)}/budget`,
    body: jsonBody(budgetBody(budget)),
    decodeResponse: (body) => decodeKey(readObject(body, "server key")),
  });
}

export function revokeServerKey(keyId: string): Promise<ServerKey> {
  return requestJson({
    method: "POST",
    path: `/server-keys/keys/${encodeURIComponent(keyId)}/revoke`,
    decodeResponse: (body) => decodeKey(readObject(body, "server key")),
  });
}

/** What an administrator types: an empty field means no limit. */
export function budgetOf(form: { calls: string; tokens: string }): KeyBudget {
  const count = (text: string) => (text.trim() === "" ? null : Number.parseInt(text, 10));
  return { budgetCalls: count(form.calls), budgetTokens: count(form.tokens) };
}

function budgetBody(budget: KeyBudget): Record<string, number | null> {
  return { budget_calls: budget.budgetCalls, budget_tokens: budget.budgetTokens };
}

export function decodeOverview(body: unknown): ServerKeysOverview {
  const overview = readObject(body, "server keys");
  return {
    available: readBoolean(overview, "available"),
    offReason: readNullableString(overview, "off_reason"),
    keys: readArray(overview["keys"], "keys", (item) => decodeKey(readObject(item, "server key"))),
    hosts: readArray(overview["hosts"], "hosts", (item) => decodeHost(readObject(item, "server host"))),
  };
}

export function decodeProviderSpend(body: unknown): ProviderSpendOverview {
  const overview = readObject(body, "provider spend");
  return {
    configured: readArray(overview["configured"], "configured", (provider) => readOneOf({ provider }, "provider", SPEND_PROVIDERS)),
    providers: readArray(overview["providers"], "providers", (item) => {
      const spend = readObject(item, "provider spend");
      return {
        provider: readOneOf(spend, "provider", SPEND_PROVIDERS),
        scope: readString(spend, "scope"),
        month: readString(spend, "month"),
        spentUsd: readNullableString(spend, "spent_usd"),
        spendProblem: readNullableString(spend, "spend_problem"),
        limitUsd: readNullableString(spend, "limit_usd"),
        limitSource: spend["limit_source"] === null ? null : readOneOf(spend, "limit_source", LIMIT_SOURCES),
        limitProblem: readNullableString(spend, "limit_problem"),
        readAt: readString(spend, "read_at"),
      };
    }),
  };
}

function decodeKey(key: JsonObject): ServerKey {
  const usage = readObject(key["usage"], "usage");
  return {
    keyId: readString(key, "key_id"),
    routeId: readString(key, "route_id"),
    api: readString(key, "api"),
    baseUrl: readNullableString(key, "base_url"),
    models: readArray(key["models"], "models", (model) => String(model)),
    hint: readString(key, "hint"),
    state: readOneOf(key, "state", KEY_STATES),
    budgetCalls: readNullableInteger(key, "budget_calls"),
    budgetTokens: readNullableInteger(key, "budget_tokens"),
    usage: {
      period: readString(usage, "period"),
      usedCalls: readInteger(usage, "used_calls"),
      usedTokens: readInteger(usage, "used_tokens"),
      reservedCalls: readInteger(usage, "reserved_calls"),
      unknownCalls: readInteger(usage, "unknown_calls"),
    },
    addedAt: readString(key, "added_at"),
  };
}

function decodeHost(host: JsonObject): ServerHost {
  return {
    userId: readString(host, "user_id"),
    userName: readNullableString(host, "user_name"),
    online: readBoolean(host, "online"),
    pairedAt: readString(host, "paired_at"),
  };
}
